/**
 *  eslint-disable max-len 
 * Validates a redirect path to prevent open redirect vulnerabilities.
 * 
 * Security rules:
 * - Must start with '/'
 * - Must NOT include protocol (http, https)
 * - Must NOT include domain names
 * - Must NOT include '//' (which could be used for protocol-relative URLs)
 * 
 * @param {string} redirectPath - The redirect path to validate
 * @returns {boolean} - True if valid, false otherwise
 */
export const validateRedirect = (redirectPath) => {
    if (!redirectPath || typeof redirectPath !== 'string') {
        return false;
    }

    const trimmed = redirectPath.trim();

    // Must start with '/'
    if (!trimmed.startsWith('/')) {
        return false;
    }

    // Must not include protocol
    if (trimmed.match(/^https?:\/\//i)) {
        return false;
    }

    // Must not include '//' (protocol-relative URLs)
    if (trimmed.includes('//')) {
        return false;
    }

    // Must not include domain-like patterns (e.g., example.com, www.example.com)
    // This regex checks for patterns like "something.com" or "www.something.com"
    // eslint-disable-next-line security/detect-unsafe-regex 
    /* eslint-disable max-len */
    if (trimmed.match(/\/[a-z0-9-]+\.(com|net|org|io|co|edu|gov|mil|biz|info|name|pro|xyz|app|dev|tech|online|site|website|store|shop|blog|news|tv|cc|ws|mobi|asia|tel|travel|jobs|mobi|xxx|aero|arpa|coop|museum|int|test|local|localhost)/i)) {
        return false;
    }

    // Must not include '@' (could be used for email-like patterns)
    if (trimmed.includes('@')) {
        return false;
    }

    // Must not include javascript: or data: schemes
    if (trimmed.match(/^(javascript|data|vbscript|file|about):/i)) {
        return false;
    }

    return true;
};

/**
 * Gets and validates the redirect path from URL search parameters.
 * 
 * @param {URLSearchParams} searchParams - URL search parameters
 * @returns {string|null} - Valid redirect path or null
 */
export const getValidatedRedirect = (searchParams) => {
    const redirect = searchParams.get('redirect');
    if (!redirect) {
        return null;
    }

    // Decode the redirect parameter
    try {
        const decoded = decodeURIComponent(redirect);
        return validateRedirect(decoded) ? decoded : null;
    } catch (error) {
        // If decoding fails, return null
        return null;
    }
};


